import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { coreApi } from '../../core/api';
import { Session, User } from '../../core/user/users.types';
import { isLoadingAuthenticationAtom, isLoggedInAtom } from './atoms';
import { logoutNative } from './utils';

const isUser = (user: User | Session): user is User => {
  return 'session' in user;
};

/**
 * Provides accesses to the authentication state
 * without side effects
 */
const useCoreAuthState = () => {
  const [isLoggedIn, setIsLoggedIn] = useAtom(isLoggedInAtom);
  const [isLoadingAuthentication] = useAtom(isLoadingAuthenticationAtom);

  const isLoading = useMemo(() => {
    if (isLoggedIn) {
      return false;
    }
    if (isLoadingAuthentication) {
      return true;
    }

    return false;
  }, [isLoadingAuthentication, isLoggedIn]);

  const logIn = useCallback(() => {
    setIsLoggedIn(true);
  }, [setIsLoggedIn]);

  const logOut = useCallback(() => {
    setIsLoggedIn(false);
  }, [setIsLoggedIn]);

  return {
    isLoggedIn: !!isLoggedIn,
    isLoading: isLoading,
    logIn,
    logOut,
  };
};

/**
 * Provides accesses to the core authentication state and actions
 */
const useCoreAuth = () => {
  const queryClient = useQueryClient();
  const { isLoggedIn, isLoading, logOut, logIn } = useCoreAuthState();

  /**
   * Handles sign in events
   * accepts either a session or user. If a user is provided, it will be used to seed the user into the query client with the key ['user']
   * sets the core auth state as logged in
   */
  const loginCoreSession = useCallback(
    (data: User | Session, callback?: (user: User) => void) => {
      if (!data) {
        return;
      }
      if (isUser(data)) {
        queryClient.setQueryData(['user'], data);
        callback?.(data);
      }
      if (data) {
        logIn();
      }
    },
    [queryClient, logIn]
  );

  /**
   * Handles sign out events
   * logs out the user from the core API
   * clears the query client
   * sets the core auth state as logged out
   */
  const logoutCoreSession = useCallback(async () => {
    try {
      return await coreApi.delete('/users/sign_out');
    } finally {
      queryClient.clear();
      logOut();
      logoutNative();
    }
  }, [queryClient, logOut]);

  return {
    logIn: loginCoreSession,
    logOut: logoutCoreSession,
    isAuthenticated: !!isLoggedIn,
    isLoading,
  };
};

export { useCoreAuth };
